import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  TextAa,
  TextB,
  Palette,
  TextHOne,
  TextHTwo,
  TextT,
  Microphone,
  MicrophoneSlash,
  Waveform,
  X,
} from "@phosphor-icons/react";
import "./RichTextEditor.css";

// Phonetic symbols for speech therapy (כתב פונטי)
const PHONETIC_SYMBOLS = {
  consonants: [
    { symbol: "p", name: "פ קשה" },
    { symbol: "b", name: "ב" },
    { symbol: "t", name: "ט" },
    { symbol: "d", name: "ד" },
    { symbol: "k", name: "כ קשה" },
    { symbol: "g", name: "ג" },
    { symbol: "ʔ", name: "עצירה גרונית" },
    { symbol: "f", name: "פ רכה" },
    { symbol: "v", name: "ב רכה" },
    { symbol: "s", name: "ס" },
    { symbol: "z", name: "ז" },
    { symbol: "ʃ", name: "ש" },
    { symbol: "ʒ", name: "ז׳" },
    { symbol: "χ", name: "ח/כ" },
    { symbol: "ʁ", name: "ר גרונית" },
    { symbol: "h", name: "ה" },
    { symbol: "m", name: "מ" },
    { symbol: "n", name: "נ" },
    { symbol: "ŋ", name: "נג" },
    { symbol: "l", name: "ל" },
    { symbol: "r", name: "ר לשונית" },
    { symbol: "j", name: "י" },
    { symbol: "w", name: "ו עיצור" },
    { symbol: "ts", name: "צ" },
    { symbol: "tʃ", name: "צ׳" },
    { symbol: "dʒ", name: "ג׳" },
  ],
  vowels: [
    { symbol: "a", name: "פתח/קמץ" },
    { symbol: "e", name: "סגול/צירה" },
    { symbol: "ɛ", name: "סגול" },
    { symbol: "i", name: "חיריק" },
    { symbol: "o", name: "חולם" },
    { symbol: "u", name: "שורוק/קובוץ" },
    { symbol: "ə", name: "שווא" },
    { symbol: "æ", name: "A פתוח" },
  ],
  diacritics: [
    { symbol: "ˈ", name: "הטעמה ראשית" },
    { symbol: "ˌ", name: "הטעמה משנית" },
    { symbol: "ː", name: "הארכה" },
    { symbol: "̃", name: "נזליות" },
    { symbol: "̥", name: "חסר קול" },
    { symbol: "ʰ", name: "שאיפה" },
    { symbol: "ʷ", name: "עיגול שפתיים" },
    { symbol: "ʲ", name: "המעה" },
  ],
  brackets: [
    { symbol: "/", name: "פונמה (התחלה)" },
    { symbol: "/", name: "פונמה (סיום)" },
    { symbol: "[", name: "צליל (התחלה)" },
    { symbol: "]", name: "צליל (סיום)" },
    { symbol: "→", name: "הופך ל" },
    { symbol: "~", name: "חילופי צלילים" },
    { symbol: "Ø", name: "השמטה" },
    { symbol: "#", name: "גבול מילה" },
  ],
};

// Predefined colors for the editor
const TEXT_COLORS = [
  { name: "שחור", value: "#1f2937" },
  { name: "אפור", value: "#6b7280" },
  { name: "כחול", value: "#3b82f6" },
  { name: "ירוק", value: "#22c55e" },
  { name: "צהוב", value: "#eab308" },
  { name: "כתום", value: "#f97316" },
  { name: "אדום", value: "#ef4444" },
  { name: "סגול", value: "#8b5cf6" },
];

// Text sizes
const TEXT_SIZES = [
  { name: "רגיל", value: "normal", icon: TextT },
  { name: "כותרת משנה", value: "subheading", icon: TextHTwo },
  { name: "כותרת", value: "heading", icon: TextHOne },
];

function RichTextEditor({ 
  value = "", 
  onChange, 
  placeholder = "הכנס טקסט...",
  enableSpeechToText = true,
  disabled = false,
  size = "normal" // "normal" | "long" | "very_long"
}) {
  const editorRef = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);
  const [showPhoneticPicker, setShowPhoneticPicker] = useState(false);
  const [activePhoneticTab, setActivePhoneticTab] = useState("consonants");
  const colorPickerRef = useRef(null);
  const sizePickerRef = useRef(null);
  const phoneticPickerRef = useRef(null);

  // Speech to Text state
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [speechError, setSpeechError] = useState(null); // Error message to display
  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false); // Track listening state for callbacks
  const networkErrorCountRef = useRef(0); // Count network errors to detect persistent issues

  // Initialize content
  useEffect(() => {
    if (editorRef.current && value && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  // Initialize Speech Recognition
  useEffect(() => {
    if (!enableSpeechToText) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.lang = 'he-IL'; // Hebrew
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      // Reset error count on successful start
      recognition.onstart = () => {
        networkErrorCountRef.current = 0;
        setSpeechError(null);
      };

      recognition.onresult = (event) => {
        // Reset error count on successful result
        networkErrorCountRef.current = 0;
        
        let finalText = '';
        let interimText = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalText += transcript;
          } else {
            interimText += transcript;
          }
        }

        // Show interim results
        setInterimTranscript(interimText);

        // Insert final text into editor
        if (finalText && editorRef.current) {
          editorRef.current.focus();
          // Insert text at cursor position
          document.execCommand('insertText', false, finalText + ' ');
          // Trigger onChange
          if (onChange) {
            onChange(editorRef.current.innerHTML);
          }
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        // Network error - count and check if persistent
        if (event.error === 'network') {
          networkErrorCountRef.current += 1;

          // After 5 network errors, show message and stop
          if (networkErrorCountRef.current >= 5) {
            setSpeechError('בעיית חיבור לשירות זיהוי הדיבור. נסה דפדפן אחר (Safari/Edge) או בדוק את חיבור האינטרנט.');
            setIsListening(false);
            isListeningRef.current = false;
            setInterimTranscript("");
            networkErrorCountRef.current = 0;
          }
          return;
        }
        
        // No speech detected - just keep listening
        if (event.error === 'no-speech') {
          return;
        }
        
        // Audio capture error - count like network errors
        if (event.error === 'audio-capture') {
          networkErrorCountRef.current += 1;

          if (networkErrorCountRef.current >= 3) {
            setSpeechError('לא ניתן לגשת למיקרופון. וודא שהמיקרופון מחובר ושניתנה הרשאה.');
            setIsListening(false);
            isListeningRef.current = false;
            setInterimTranscript("");
            networkErrorCountRef.current = 0;
          }
          return;
        }
        
        // Service not allowed / bad grammar - continue
        if (event.error === 'service-not-allowed' || event.error === 'bad-grammar') {
          return;
        }
        
        // Permission denied - show message and stop
        if (event.error === 'not-allowed') {
          setSpeechError('לא ניתנה הרשאה למיקרופון. לחץ על סמל המנעול בשורת הכתובת ואפשר גישה למיקרופון.');
          setIsListening(false);
          isListeningRef.current = false;
          setInterimTranscript("");
          return;
        }
        
        // Aborted - user stopped it manually, don't restart
        if (event.error === 'aborted') {
          return;
        }
        
        // Unknown error - log but continue
        console.warn('Unknown speech recognition error, continuing...', event.error);
      };

      recognition.onend = () => {
        // Auto-restart if still supposed to be listening (handles browser auto-stop)
        if (isListeningRef.current) {
          // Delay before restarting to avoid rapid restarts and give network time to recover
          setTimeout(() => {
            if (isListeningRef.current && recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                console.error('Failed to restart recognition:', e);
                // Try once more after a longer delay
                setTimeout(() => {
                  if (isListeningRef.current && recognitionRef.current) {
                    try {
                      recognitionRef.current.start();
                    } catch (err) {
                      console.error('Failed to restart recognition after retry:', err);
                      setIsListening(false);
                      isListeningRef.current = false;
                      setInterimTranscript("");
                    }
                  }
                }, 500);
              }
            }
          }, 250);
        } else {
          setInterimTranscript("");
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        isListeningRef.current = false;
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [enableSpeechToText, onChange]);

  // Sync listening ref with state
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  // Start speech recognition
  const startSpeechRecognition = useCallback(() => {
    if (!speechSupported || !recognitionRef.current) {
      setSpeechError('הדפדפן שלך לא תומך בזיהוי דיבור. נסה להשתמש ב-Chrome, Edge או Safari.');
      return;
    }

    if (isListening) return; // Already listening

    try {
      // Reset error count and clear any previous error
      networkErrorCountRef.current = 0;
      setSpeechError(null);
      
      // Focus on editor first so text will be inserted there
      editorRef.current?.focus();
      
      // Set state before starting
      isListeningRef.current = true;
      setIsListening(true);
      
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      // Might already be running, try to restart
      try {
        recognitionRef.current.stop();
        setTimeout(() => {
          if (recognitionRef.current) {
            isListeningRef.current = true;
            setIsListening(true);
            recognitionRef.current.start();
          }
        }, 150);
      } catch (e) {
        console.error('Failed to restart speech recognition:', e);
        setIsListening(false);
        isListeningRef.current = false;
      }
    }
  }, [isListening, speechSupported]);

  // Stop speech recognition
  const stopSpeechRecognition = useCallback(() => {
    if (!recognitionRef.current) return;
    
    // Set state before stopping
    isListeningRef.current = false;
    setIsListening(false);
    setInterimTranscript("");
    
    try {
      recognitionRef.current.stop();
    } catch (e) {
      // Ignore
    }
  }, []);

  // Toggle speech recognition
  const toggleSpeechRecognition = useCallback(() => {
    if (isListening) {
      stopSpeechRecognition();
    } else {
      startSpeechRecognition();
    }
  }, [isListening, startSpeechRecognition, stopSpeechRecognition]);

  // Close pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
      if (sizePickerRef.current && !sizePickerRef.current.contains(event.target)) {
        setShowSizePicker(false);
      }
      if (phoneticPickerRef.current && !phoneticPickerRef.current.contains(event.target)) {
        setShowPhoneticPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInput = () => {
    if (editorRef.current && onChange && !disabled) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    if (disabled) return;
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const applyColor = (color) => {
    execCommand("foreColor", color);
    setShowColorPicker(false);
  };

  const applySize = (size) => {
    // Remove any existing size classes
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      if (range.collapsed) {
        setShowSizePicker(false);
        return;
      }

      // Wrap selection in span with size class
      const span = document.createElement("span");
      span.className = `text-size-${size}`;
      
      try {
        range.surroundContents(span);
        handleInput();
      } catch (e) {
        // If surroundContents fails, try a different approach
        const content = range.extractContents();
        span.appendChild(content);
        range.insertNode(span);
        handleInput();
      }
    }
    setShowSizePicker(false);
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const insertPhoneticSymbol = (symbol) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand("insertText", false, symbol);
    handleInput();
  };

  const phoneticTabs = [
    { id: "consonants", label: "עיצורים" },
    { id: "vowels", label: "תנועות" },
    { id: "diacritics", label: "סימנים" },
    { id: "brackets", label: "תחביר" },
  ];

  return (
    <>
      <div className={`rich-text-editor rich-text-editor-${size} ${disabled ? 'rich-text-editor-disabled' : ''}`}>
        {/* Toolbar */}
        <div className="editor-toolbar">
          {/* Bold */}
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("bold")}
            title="מודגש"
            disabled={disabled}
          >
            <TextB size={18} weight="bold" />
          </button>

          {/* Text Size */}
          <div className="toolbar-dropdown" ref={sizePickerRef}>
            <button
              type="button"
              className="toolbar-btn"
              onClick={() => {
                if (disabled) return;
                setShowSizePicker(!showSizePicker);
                setShowColorPicker(false);
                setShowPhoneticPicker(false);
              }}
              title="גודל טקסט"
              disabled={disabled}
            >
              <TextAa size={18} weight="regular" />
            </button>
            {showSizePicker && (
              <div className="dropdown-menu size-menu">
                {TEXT_SIZES.map((size) => (
                  <button
                    key={size.value}
                    type="button"
                    className="dropdown-item"
                    onClick={() => applySize(size.value)}
                  >
                    <size.icon size={16} weight="regular" />
                    <span>{size.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Text Color */}
          <div className="toolbar-dropdown" ref={colorPickerRef}>
            <button
              type="button"
              className="toolbar-btn"
              onClick={() => {
                if (disabled) return;
                setShowColorPicker(!showColorPicker);
                setShowSizePicker(false);
                setShowPhoneticPicker(false);
              }}
              title="צבע טקסט"
              disabled={disabled}
            >
              <Palette size={18} weight="regular" />
            </button>
            {showColorPicker && (
              <div className="dropdown-menu color-menu">
                <div className="color-grid">
                  {TEXT_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className="color-btn"
                      style={{ backgroundColor: color.value }}
                      onClick={() => applyColor(color.value)}
                      title={color.name}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Phonetic Script */}
          <div className="toolbar-dropdown" ref={phoneticPickerRef}>
            <button
              type="button"
              className="toolbar-btn phonetic-btn"
              onClick={() => {
                if (disabled) return;
                setShowPhoneticPicker(!showPhoneticPicker);
                setShowColorPicker(false);
                setShowSizePicker(false);
              }}
              title="כתב פונטי"
              disabled={disabled}
            >
              <Waveform size={18} weight="regular" />
            </button>
            {showPhoneticPicker && (
              <div className="dropdown-menu phonetic-menu">
                <div className="phonetic-header">
                  <span className="phonetic-title">כתב פונטי (IPA)</span>
                </div>
                <div className="phonetic-tabs">
                  {phoneticTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`phonetic-tab ${activePhoneticTab === tab.id ? "active" : ""}`}
                      onClick={() => setActivePhoneticTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="phonetic-grid">
                  {PHONETIC_SYMBOLS[activePhoneticTab].map((item, index) => (
                    <button
                      key={index}
                      type="button"
                      className="phonetic-symbol-btn"
                      onClick={() => insertPhoneticSymbol(item.symbol)}
                      title={item.name}
                    >
                      <span className="symbol">{item.symbol}</span>
                      <span className="symbol-name">{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Speech to Text - show if enabled and not disabled */}
          {enableSpeechToText && !disabled && (
            <button
              type="button"
              className={`toolbar-btn speech-to-text-btn ${isListening ? 'recording' : ''}`}
              onClick={toggleSpeechRecognition}
              title={isListening ? "עצור הקלטה" : "דיבור לטקסט"}
            >
              {isListening ? (
                <MicrophoneSlash size={18} weight="fill" />
              ) : (
                <Microphone size={18} weight="regular" />
              )}
            </button>
          )}
        </div>

        {/* Editor Area */}
        <div
          ref={editorRef}
          className="editor-content"
          contentEditable={!disabled}
          onInput={handleInput}
          onPaste={handlePaste}
          data-placeholder={placeholder}
          dir="rtl"
        />
      </div>

      {/* Speech Recognition Overlay - Full screen when listening */}
      {isListening && createPortal(
        <div 
          className="speech-overlay"
          onClick={stopSpeechRecognition}
          onTouchEnd={(e) => {
            e.preventDefault();
            stopSpeechRecognition();
          }}
        >
          <div className="speech-overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="speech-overlay-microphone">
              <div className="speech-mic-waves">
                <div className="speech-wave"></div>
                <div className="speech-wave"></div>
                <div className="speech-wave"></div>
              </div>
              <Microphone size={48} weight="fill" />
            </div>
            <div className="speech-overlay-text">
              {interimTranscript ? (
                <span className="speech-interim">{interimTranscript}</span>
              ) : (
                <span className="speech-listening">מאזין...</span>
              )}
            </div>
            <button 
              className="speech-stop-btn"
              onClick={stopSpeechRecognition}
              type="button"
            >
              <X size={24} weight="bold" />
              <span>סיים הקלטה</span>
            </button>
            <p className="speech-hint">לחץ בכל מקום על המסך לסיום</p>
          </div>
        </div>,
        document.body
      )}

      {/* Speech Error Modal */}
      {speechError && createPortal(
        <div 
          className="speech-error-overlay"
          onClick={() => setSpeechError(null)}
        >
          <div className="speech-error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="speech-error-icon">
              <MicrophoneSlash size={40} weight="fill" />
            </div>
            <h3 className="speech-error-title">בעיה בזיהוי דיבור</h3>
            <p className="speech-error-message">{speechError}</p>
            <div className="speech-error-tips">
              <p><strong>טיפים:</strong></p>
              <ul>
                <li>נסה דפדפן Safari (עובד הכי טוב בעברית)</li>
                <li>וודא שיש חיבור אינטרנט יציב</li>
                <li>בדוק שניתנה הרשאה למיקרופון</li>
              </ul>
            </div>
            <button 
              className="speech-error-close-btn"
              onClick={() => setSpeechError(null)}
              type="button"
            >
              הבנתי
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default RichTextEditor;

